import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns/format';
import { FileSpreadsheet, FileText, Search, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';
import Header from '../Header';
import Sidebar from '../Sidebar';
import watermarkImage from '../assets/coverzone-watermark.png';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Maps each report checkbox key to its feature permission key
const REPORT_PERMISSION_MAP: Record<string, string> = {
  instructions: 'instructions',
  clientInfo: 'client_info',
  claims: 'claims',
  quotations: 'quotes',
  emails: 'email',
  renewals: 'renewals',
  whatsapps: 'whatsapp',
  returnDebits: 'return_debits',
  products: 'products',
  policies: 'policies',
};

interface ReportFilter {
  dateRange: {
    createdDate: { enabled: boolean; start: string; end: string };
    updatedDate: { enabled: boolean; start: string; end: string };
  };
  instructions: boolean;
  clientInfo: boolean;
  claims: boolean;
  consultant: string;
  quotations: boolean;
  emails: boolean;
  renewals: boolean;
  whatsapps: boolean;
  returnDebits: boolean;
  products: boolean;
  policies: boolean;
}

interface InstructionReport {
  id: number;
  client_id: number;
  instruction: string;
  reference_number: string;
  policy_number: string;
  instruction_type: string;
  instruction_method: string;
  client_name: string;
  assigned_to_name: string;
  consultant_name: string;
  created_date: string;
  updated_date: string;
  status: string;
  date_received: string;
  date_sign_off: string;
  signed_off_by: string;
}

interface ClaimReport {
  id: number;
  client_id: number;
  reference_number: string;
  description: string;
  claim_type: string;
  claim_method: string;
  status: string;
  date_received: string;
  claims_reported_date: string;
  date_sign_off: string;
  signed_off_by: string;
  underwriters_claim_number: string;
  total_cost_of_claim: number;
  claim_excess: number;
  created_date: string;
  updated_date: string;
  client_name: string;
  policy_number: string;
  consultant_name: string;
  consultant_surname: string;
  employee_name: string;
  employee_surname: string;
}

interface QuotationReport {
  id: number;
  client_id: number;
  reference: string;
  status: string;
  consultant_id: number | null;
  administrator_id: number | null;
  underwriter_id: string | null;
  duration: string | null;
  notes: string | null;
  premium: string | number | null;
  broker_fee: string | number | null;
  management_fee: string | number | null;
  inception_date: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  consultant_name: string | null;
  consultant_surname: string | null;
  administrator_name: string | null;
  underwriter_name: string | null;
}

interface RenewalReport {
  id: number;
  client_id: number;
  policy_id: number | null;
  reference_number: string;
  renewal_method: string;
  description: string;
  status: string;
  date_received: string | null;
  date_sign_off: string | null;
  signed_off_by: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  policy_number: string | null;
  consultant_name: string | null;
  consultant_surname: string | null;
  employee_name: string | null;
  employee_surname: string | null;
}

interface ReturnDebitReport {
  id: number;
  client_id: number;
  policy_id: number | null;
  reference_number: string;
  return_debit_method: string;
  description: string;
  status: string;
  date_received: string | null;
  date_sign_off: string | null;
  signed_off_by: string | null;
  resolution_method: string | null;
  date_forced_debit: string | null;
  date_received_forced: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  policy_number: string | null;
  consultant_name: string | null;
  consultant_surname: string | null;
  employee_name: string | null;
  employee_surname: string | null;
}

interface PolicyReport {
  id: number;
  client_id: number;
  policy_number: string;
  product_name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  premium_amount: number | null;
  insurer: string | null;
  cancellation_date: string | null;
  cancellation_reason: string | null;
  renewal_date: string | null;
  premium_collection_date: string | null;
  quote_id: number | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  consultant_id: number | null;
  consultant_name: string | null;
  consultant_surname: string | null;
}

interface ClientInfoReport {
  id: number;
  name: string;
  surname: string;
  email: string;
  mobile_number: string | null;
  telephone_number: string | null;
  client_number: string | null;
  organization: string | null;
  personal: boolean | null;
  commercial: boolean | null;
  medical: boolean | null;
  status: string | null;
  date_of_birth: string | null;
  id_passport_number: string | null;
  created_date: string;
  updated_date: string;
  consultant_id: number | null;
  consultant_name: string | null;
  consultant_surname: string | null;
}

interface EmailReport {
  id: number;
  client_id: number;
  sent_to: string | null;
  subject: string | null;
  message: string | null;
  employee: string | null;
  delivery_status: string | null;
  ref_no: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  consultant_id: number | null;
  consultant_name: string | null;
  consultant_surname: string | null;
}

interface WhatsAppReport {
  id: number;
  client_id: number;
  sent_to: string | null;
  message: string | null;
  employee: string | null;
  delivery_status: string | null;
  ref_no: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  consultant_id: number | null;
  consultant_name: string | null;
  consultant_surname: string | null;
}

interface ProductReport {
  product_type: string;
  product_id: number;
  policy_id: number;
  client_id: number;
  policy_number: string;
  insured_value: number | null;
  premium: number | null;
  status: string | null;
  cover_type: string | null;
  description: string | null;
  created_date: string;
  updated_date: string;
  client_name: string;
  consultant_id: number | null;
  consultant_name: string | null;
  consultant_surname: string | null;
}

type ReportRow = InstructionReport | ClaimReport | QuotationReport | RenewalReport | ReturnDebitReport | PolicyReport | ClientInfoReport | EmailReport | WhatsAppReport | ProductReport;

const Reports = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string; surname?: string } | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

    const [filters, setFilters] = useState<ReportFilter>({
    dateRange: {
      createdDate: { enabled: false, start: '', end: '' },
      updatedDate: { enabled: false, start: '', end: '' }
    },
    instructions: false,
    clientInfo: false,
    claims: false,
    consultant: '',
    quotations: false,
    emails: false,
    renewals: false,
    whatsapps: false,
    returnDebits: false,
    products: false,
    policies: false
  });

  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportType, setReportType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [consultants, setConsultants] = useState<Array<{ id: number; name: string }>>([]);

    useEffect(() => {
    const loadOptions = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: HeadersInit = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const consultantsRes = await fetch(`${API_URL}/api/reports/consultants`, { headers, credentials: 'include' });
        if (consultantsRes.ok) setConsultants(await consultantsRes.json());
        else setConsultants([]);
      } catch (error) {
        console.error('Error loading options:', error);
        setConsultants([]);
      }
    };
    loadOptions();
  }, []);

  // If the currently-selected report type is not permitted, uncheck it.
  useEffect(() => {
    const activeKey = (Object.keys(REPORT_PERMISSION_MAP) as Array<keyof typeof REPORT_PERMISSION_MAP>)
      .find(k => filters[k as keyof ReportFilter] === true);
    if (activeKey) {
      const featureKey = REPORT_PERMISSION_MAP[activeKey];
      if (!can(featureKey, 'read')) {
        setFilters(prev => ({ ...prev, [activeKey]: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You must be logged in to generate reports');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();

      if (filters.dateRange.createdDate.enabled) {
        params.append('createdDateStart', filters.dateRange.createdDate.start);
        params.append('createdDateEnd', filters.dateRange.createdDate.end);
      }
      if (filters.dateRange.updatedDate.enabled) {
        params.append('updatedDateStart', filters.dateRange.updatedDate.start);
        params.append('updatedDateEnd', filters.dateRange.updatedDate.end);
      }
      
      if (filters.consultant) params.append('consultantId', filters.consultant);

      let endpoint = '';
      let currentReportType = '';

      if (filters.instructions) {
        endpoint = '/api/reports/instructions';
        currentReportType = 'Instructions';
      } else if (filters.claims) {
        endpoint = '/api/reports/claims';
        currentReportType = 'Claims';
      } else if (filters.quotations) {
        endpoint = '/api/reports/quotations';
        currentReportType = 'Quotations';
      } else if (filters.renewals) {
        endpoint = '/api/reports/renewals';
        currentReportType = 'Renewals';
      } else if (filters.returnDebits) {
        endpoint = '/api/reports/return-debits';
        currentReportType = 'Return Debits';
      } else if (filters.emails) {
        endpoint = '/api/reports/emails';
        currentReportType = 'Emails';
      } else if (filters.whatsapps) {
        endpoint = '/api/reports/whatsapps';
        currentReportType = 'WhatsApps';
      } else if (filters.products) {
        endpoint = '/api/reports/products';
        currentReportType = 'Products';
      } else if (filters.policies) {
        endpoint = '/api/reports/policies';
        currentReportType = 'Policies';
      } else if (filters.clientInfo) {
        endpoint = '/api/reports/client-info';
        currentReportType = 'Client Info';
      } else {
        alert('Please select at least one report type');
        setLoading(false);
        return;
      }

      setReportType(currentReportType);

      const url = `${API_URL}${endpoint}?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setReportData(data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert(`Failed to generate report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const parseMoney = (val: any): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const cleaned = String(val).replace(/,/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  const formatMoney = (val: any): string => {
    const n = parseMoney(val);
    return n === null
      ? '-'
      : `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatStatus = (status: string): string => {
    if (!status) return 'Active';
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatResolution = (method: string | null): string => {
    if (!method) return '-';
    if (method === 'double_deduction') return 'Double Deduction';
    if (method === 'forced_debit_order') return 'Forced Debit Order';
    return formatStatus(method);
  };

  const consultantDisplay = (name: string | null | undefined, surname: string | null | undefined): string => {
    if (!name) return 'Unassigned';
    return `${name} ${surname || ''}`.trim();
  };

  const handleInstructionClick = (instructionId: number, clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { instructionId: instructionId.toString() } });
  };

  const handleClaimClick = (claimId: number, clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'claims', claimId: claimId.toString() } });
  };

  const handleQuoteClick = (quoteId: number, clientId: number) => {
    navigate(`/quotes/master/${clientId}/${quoteId}`);
  };

  const handleRenewalClick = (renewalId: number, clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'renewals', renewalId: renewalId.toString() } });
  };

  const handleReturnDebitClick = (returnDebitId: number, clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'returnDebits', returnDebitId: returnDebitId.toString() } });
  };

  const handlePolicyClick = (policyId: number, clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'policies', policyId: policyId.toString() } });
  };

  const handleClientClick = (clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'clientInfo' } });
  };

    const handleEmailClick = (clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'email' } });
  };

  const handleWhatsAppClick = (clientId: number) => {
    navigate(`/personal/${clientId}`, { state: { activeTab: 'whatsapp' } });
  };

  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert('No data to export');
      return;
    }

    let exportData: any[] = [];
    let fileName = '';

    if (reportType === 'Claims') {
      exportData = (reportData as ClaimReport[]).map(row => ({
        'Reference': row.reference_number || '',
        'Client': row.client_name || '',
        'Policy Number': row.policy_number || '',
        'Claim Type': row.claim_type || '',
        'Claim Method': row.claim_method || '',
        'Description': row.description || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Status': row.status || '',
        'Claims Reported Date': row.claims_reported_date ? new Date(row.claims_reported_date).toLocaleDateString() : '',
        'Date Received': row.date_received ? new Date(row.date_received).toLocaleDateString() : '',
        'Underwriter Claim No': row.underwriters_claim_number || '',
        'Total Cost of Claim': row.total_cost_of_claim || 0,
        'Claim Excess': row.claim_excess || 0,
        'Date Sign Off': row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : '',
        'Signed Off By': row.signed_off_by || '',
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `claims-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'Quotations') {
      exportData = (reportData as QuotationReport[]).map(row => ({
        'Reference': row.reference || '',
        'Client': row.client_name || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Status': row.status || '',
        'Premium': parseMoney(row.premium) ?? '',
        'Broker Fee': parseMoney(row.broker_fee) ?? '',
        'Management Fee': parseMoney(row.management_fee) ?? '',
        'Duration': row.duration || '',
        'Inception Date': row.inception_date ? new Date(row.inception_date).toLocaleDateString() : '',
        'Administrator': row.administrator_name || '',
        'Underwriter': row.underwriter_name || '',
        'Notes': row.notes || '',
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `quotations-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'Renewals') {
      exportData = (reportData as RenewalReport[]).map(row => ({
        'Reference': row.reference_number || '',
        'Client': row.client_name || '',
        'Policy Number': row.policy_number || '',
        'Renewal Method': row.renewal_method || '',
        'Description': row.description || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Status': row.status || '',
        'Date Received': row.date_received ? new Date(row.date_received).toLocaleDateString() : '',
        'Date Sign Off': row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : '',
        'Signed Off By': row.signed_off_by || '',
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `renewals-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'Return Debits') {
      exportData = (reportData as ReturnDebitReport[]).map(row => ({
        'Reference': row.reference_number || '',
        'Client': row.client_name || '',
        'Policy Number': row.policy_number || '',
        'Return Debit Method': row.return_debit_method || '',
        'Description': row.description || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Resolution': formatResolution(row.resolution_method),
        'Status': row.status || '',
        'Date Received': row.date_received ? new Date(row.date_received).toLocaleDateString() : '',
        'Date Forced Debit': row.date_forced_debit ? new Date(row.date_forced_debit).toLocaleDateString() : '',
        'Date Received Forced': row.date_received_forced ? new Date(row.date_received_forced).toLocaleDateString() : '',
        'Date Sign Off': row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : '',
        'Signed Off By': row.signed_off_by || '',
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `return-debits-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        } else if (reportType === 'Policies') {
      exportData = (reportData as PolicyReport[]).map(row => ({
        'Policy Number': row.policy_number || '',
        'Client': row.client_name || '',
        'Product': row.product_name || '',
        'Status': row.status || '',
        'Insurer': row.insurer || '',
        'Start Date': row.start_date ? new Date(row.start_date).toLocaleDateString() : '',
        'End Date': row.end_date ? new Date(row.end_date).toLocaleDateString() : '',
        'Premium': row.premium_amount ?? '',
        'Renewal Date': row.renewal_date ? new Date(row.renewal_date).toLocaleDateString() : '',
        'Premium Collection': row.premium_collection_date ? new Date(row.premium_collection_date).toLocaleDateString() : '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Cancellation Date': row.cancellation_date ? new Date(row.cancellation_date).toLocaleDateString() : '',
        'Cancellation Reason': row.cancellation_reason || '',
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `policies-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'Client Info') {
      exportData = (reportData as ClientInfoReport[]).map(row => ({
        'Client No': row.client_number || row.id,
        'Name': `${row.name || ''} ${row.surname || ''}`.trim(),
        'Email': row.email || '',
        'Mobile': row.mobile_number || '',
        'Telephone': row.telephone_number || '',
        'Organization': row.organization || '',
        'Type': row.personal ? 'Personal' : row.commercial ? 'Commercial' : row.medical ? 'Medical' : '-',
        'Status': row.status || '',
        'Date of Birth': row.date_of_birth ? new Date(row.date_of_birth).toLocaleDateString() : '',
        'ID / Passport': row.id_passport_number || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Updated Date': row.updated_date ? new Date(row.updated_date).toLocaleString() : ''
      }));
      fileName = `client-info-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        } else if (reportType === 'Emails') {
      exportData = (reportData as EmailReport[]).map(row => ({
        'Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Client': row.client_name || '',
        'Sent To': row.sent_to || '',
        'Subject': row.subject || '',
        'Message': row.message || '',
        'Employee': row.employee || '',
        'Delivery Status': row.delivery_status || '',
        'Ref No': row.ref_no || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname)
      }));
      fileName = `emails-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'WhatsApps') {
      exportData = (reportData as WhatsAppReport[]).map(row => ({
        'Date': row.created_date ? new Date(row.created_date).toLocaleString() : '',
        'Client': row.client_name || '',
        'Sent To': row.sent_to || '',
        'Message': row.message || '',
        'Employee': row.employee || '',
        'Delivery Status': row.delivery_status || '',
        'Ref No': row.ref_no || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname)
      }));
      fileName = `whatsapps-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else if (reportType === 'Products') {
      exportData = (reportData as ProductReport[]).map(row => ({
        'Policy Number': row.policy_number || '',
        'Client': row.client_name || '',
        'Product Type': row.product_type || '',
        'Description': row.description || '',
        'Insured Value': row.insured_value ?? '',
        'Premium': row.premium ?? '',
        'Cover Type': row.cover_type || '',
        'Status': row.status || '',
        'Consultant': consultantDisplay(row.consultant_name, row.consultant_surname),
        'Created Date': row.created_date ? new Date(row.created_date).toLocaleString() : ''
      }));
      fileName = `products-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    } else {
      exportData = (reportData as InstructionReport[]).map(row => ({
        'Description': row.instruction || '',
        'Reference': row.reference_number || '',
        'Type': row.instruction_type || '',
        'Method': row.instruction_method || '',
        'Client': row.client_name || '',
        'Assigned To': row.assigned_to_name || '',
        'Status': row.status || '',
        'Created Date': row.created_date ? format(new Date(row.created_date), 'yyyy-MM-dd HH:mm') : '',
        'Updated Date': row.updated_date ? format(new Date(row.updated_date), 'yyyy-MM-dd HH:mm') : '',
        'Date Received': row.date_received ? new Date(row.date_received).toLocaleDateString() : '',
        'Date Sign Off': row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : '',
        'Signed Off By': row.signed_off_by || ''
      }));
      fileName = `instructions-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, `${reportType} Report`);
    XLSX.writeFile(wb, fileName);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export');
      return;
    }

    let headers: string[] = [];
    let rows: string[][] = [];
    let fileName = '';

    const q = (val: any) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    if (reportType === 'Claims') {
      headers = ['Reference','Client','Policy Number','Claim Type','Claim Method','Description','Consultant','Status','Claims Reported Date','Date Received','Underwriter Claim No','Total Cost of Claim','Claim Excess','Date Sign Off','Signed Off By','Created Date','Updated Date'];
      rows = (reportData as ClaimReport[]).map(row => [
        q(row.reference_number), q(row.client_name), q(row.policy_number), q(row.claim_type), q(row.claim_method),
        q(row.description), q(consultantDisplay(row.consultant_name, row.consultant_surname)), q(row.status),
        q(row.claims_reported_date ? new Date(row.claims_reported_date).toLocaleDateString() : ''),
        q(row.date_received ? new Date(row.date_received).toLocaleDateString() : ''),
        q(row.underwriters_claim_number), q(row.total_cost_of_claim || 0), q(row.claim_excess || 0),
        q(row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : ''),
        q(row.signed_off_by),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `claims-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'Quotations') {
      headers = ['Reference','Client','Consultant','Status','Premium','Broker Fee','Management Fee','Duration','Inception Date','Administrator','Underwriter','Notes','Created Date','Updated Date'];
      rows = (reportData as QuotationReport[]).map(row => [
        q(row.reference), q(row.client_name), q(consultantDisplay(row.consultant_name, row.consultant_surname)),
        q(row.status), q(parseMoney(row.premium) ?? ''), q(parseMoney(row.broker_fee) ?? ''), q(parseMoney(row.management_fee) ?? ''),
        q(row.duration), q(row.inception_date ? new Date(row.inception_date).toLocaleDateString() : ''),
        q(row.administrator_name), q(row.underwriter_name), q(row.notes),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `quotations-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'Renewals') {
      headers = ['Reference','Client','Policy Number','Renewal Method','Description','Consultant','Status','Date Received','Date Sign Off','Signed Off By','Created Date','Updated Date'];
      rows = (reportData as RenewalReport[]).map(row => [
        q(row.reference_number), q(row.client_name), q(row.policy_number), q(row.renewal_method), q(row.description),
        q(consultantDisplay(row.consultant_name, row.consultant_surname)), q(row.status),
        q(row.date_received ? new Date(row.date_received).toLocaleDateString() : ''),
        q(row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : ''),
        q(row.signed_off_by),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `renewals-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'Return Debits') {
      headers = ['Reference','Client','Policy Number','Return Debit Method','Description','Consultant','Resolution','Status','Date Received','Date Forced Debit','Date Received Forced','Date Sign Off','Signed Off By','Created Date','Updated Date'];
      rows = (reportData as ReturnDebitReport[]).map(row => [
        q(row.reference_number), q(row.client_name), q(row.policy_number), q(row.return_debit_method), q(row.description),
        q(consultantDisplay(row.consultant_name, row.consultant_surname)), q(formatResolution(row.resolution_method)), q(row.status),
        q(row.date_received ? new Date(row.date_received).toLocaleDateString() : ''),
        q(row.date_forced_debit ? new Date(row.date_forced_debit).toLocaleDateString() : ''),
        q(row.date_received_forced ? new Date(row.date_received_forced).toLocaleDateString() : ''),
        q(row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : ''),
        q(row.signed_off_by),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `return-debits-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        } else if (reportType === 'Policies') {
      headers = ['Policy Number','Client','Product','Status','Insurer','Start Date','End Date','Premium','Renewal Date','Premium Collection','Consultant','Cancellation Date','Cancellation Reason','Created Date','Updated Date'];
      rows = (reportData as PolicyReport[]).map(row => [
        q(row.policy_number), q(row.client_name), q(row.product_name), q(row.status), q(row.insurer),
        q(row.start_date ? new Date(row.start_date).toLocaleDateString() : ''),
        q(row.end_date ? new Date(row.end_date).toLocaleDateString() : ''),
        q(row.premium_amount ?? ''),
        q(row.renewal_date ? new Date(row.renewal_date).toLocaleDateString() : ''),
        q(row.premium_collection_date ? new Date(row.premium_collection_date).toLocaleDateString() : ''),
        q(consultantDisplay(row.consultant_name, row.consultant_surname)),
        q(row.cancellation_date ? new Date(row.cancellation_date).toLocaleDateString() : ''),
        q(row.cancellation_reason),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `policies-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'Client Info') {
      headers = ['Client No','Name','Email','Mobile','Telephone','Organization','Type','Status','Date of Birth','ID / Passport','Consultant','Created Date','Updated Date'];
      rows = (reportData as ClientInfoReport[]).map(row => [
        q(row.client_number || row.id),
        q(`${row.name || ''} ${row.surname || ''}`.trim()),
        q(row.email), q(row.mobile_number), q(row.telephone_number), q(row.organization),
        q(row.personal ? 'Personal' : row.commercial ? 'Commercial' : row.medical ? 'Medical' : '-'),
        q(row.status),
        q(row.date_of_birth ? new Date(row.date_of_birth).toLocaleDateString() : ''),
        q(row.id_passport_number),
        q(consultantDisplay(row.consultant_name, row.consultant_surname)),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.updated_date ? new Date(row.updated_date).toLocaleString() : '')
      ]);
      fileName = `client-info-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        } else if (reportType === 'Emails') {
      headers = ['Date','Client','Sent To','Subject','Message','Employee','Delivery Status','Ref No','Consultant'];
      rows = (reportData as EmailReport[]).map(row => [
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.client_name), q(row.sent_to), q(row.subject), q(row.message), q(row.employee),
        q(row.delivery_status), q(row.ref_no),
        q(consultantDisplay(row.consultant_name, row.consultant_surname))
      ]);
      fileName = `emails-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'WhatsApps') {
      headers = ['Date','Client','Sent To','Message','Employee','Delivery Status','Ref No','Consultant'];
      rows = (reportData as WhatsAppReport[]).map(row => [
        q(row.created_date ? new Date(row.created_date).toLocaleString() : ''),
        q(row.client_name), q(row.sent_to), q(row.message), q(row.employee),
        q(row.delivery_status), q(row.ref_no),
        q(consultantDisplay(row.consultant_name, row.consultant_surname))
      ]);
      fileName = `whatsapps-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else if (reportType === 'Products') {
      headers = ['Policy Number','Client','Product Type','Description','Insured Value','Premium','Cover Type','Status','Consultant','Created Date'];
      rows = (reportData as ProductReport[]).map(row => [
        q(row.policy_number), q(row.client_name), q(row.product_type), q(row.description),
        q(row.insured_value ?? ''), q(row.premium ?? ''), q(row.cover_type), q(row.status),
        q(consultantDisplay(row.consultant_name, row.consultant_surname)),
        q(row.created_date ? new Date(row.created_date).toLocaleString() : '')
      ]);
      fileName = `products-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    } else {
      headers = ['Description','Reference','Type','Method','Client','Assigned To','Status','Created Date','Updated Date','Date Received','Date Sign Off','Signed Off By'];
      rows = (reportData as InstructionReport[]).map(row => [
        q(row.instruction), q(row.reference_number), q(row.instruction_type), q(row.instruction_method),
        q(row.client_name), q(row.assigned_to_name), q(row.status),
        q(row.created_date ? format(new Date(row.created_date), 'yyyy-MM-dd HH:mm') : ''),
        q(row.updated_date ? format(new Date(row.updated_date), 'yyyy-MM-dd HH:mm') : ''),
        q(row.date_received ? new Date(row.date_received).toLocaleDateString() : ''),
        q(row.date_sign_off ? new Date(row.date_sign_off).toLocaleDateString() : ''),
        q(row.signed_off_by)
      ]);
      fileName = `instructions-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    }

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Shared status chip renderer
  const statusChip = (status: string, context: 'claim' | 'renewal' | 'returnDebit' | 'quote' | 'instruction') => {
    const s = status?.toLowerCase() || '';
    let classes = 'bg-gray-100 text-gray-800';
    if (s === 'completed') classes = 'bg-green-100 text-green-800';
    else if (s === 'cancelled') classes = 'bg-red-100 text-red-800';
    else if (s === 'pending' || s === 'registered') classes = 'bg-yellow-100 text-yellow-800';
    else if (s === 'draft') classes = 'bg-gray-100 text-gray-800';
    else if (s === 'sent' || s === 'sent_to_insurer' || s === 'send_to_client' || s === 'schedule_sent_to_client') classes = 'bg-blue-100 text-blue-800';
    else if (s === 'accepted') classes = 'bg-green-100 text-green-800';
    else if (s === 'declined') classes = 'bg-red-100 text-red-800';
    else if (s === 'expired') classes = 'bg-yellow-100 text-yellow-800';
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${classes}`}>
        {formatStatus(status)}
      </span>
    );
  };

  const th = (label: string) => (
    <th key={label} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{label}</th>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img src={watermarkImage} alt="Watermark" className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none" />
      </div>

      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header title="Reports" user={user} toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <div className="flex-grow p-6">
  <div className="max-w-7xl mx-auto">
    <div className="mb-6">
      <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
      <p className="text-sm text-gray-500">Select the report types and filters you want to include, then click Generate Report</p>
    </div>

    {!can('reports', 'read') ? (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-500">You do not have permission to access Reports.</p>
      </div>
    ) : (
      <>

            {/* FILTERS */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" checked={filters.dateRange.createdDate.enabled}
                      onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, createdDate: { ...filters.dateRange.createdDate, enabled: e.target.checked } } })}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <label className="text-sm font-medium text-gray-700">Created Date</label>
                  </div>
                  {filters.dateRange.createdDate.enabled && (
                    <div className="flex flex-col gap-2 ml-6">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">From:</span>
                        <input type="date" value={filters.dateRange.createdDate.start}
                          onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, createdDate: { ...filters.dateRange.createdDate, start: e.target.value } } })}
                          className="px-2 py-1 text-sm border border-gray-300 rounded" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">To:</span>
                        <input type="date" value={filters.dateRange.createdDate.end}
                          onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, createdDate: { ...filters.dateRange.createdDate, end: e.target.value } } })}
                          className="px-2 py-1 text-sm border border-gray-300 rounded" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" checked={filters.dateRange.updatedDate.enabled}
                      onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, updatedDate: { ...filters.dateRange.updatedDate, enabled: e.target.checked } } })}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <label className="text-sm font-medium text-gray-700">Updated Date</label>
                  </div>
                  {filters.dateRange.updatedDate.enabled && (
                    <div className="flex flex-col gap-2 ml-6">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">From:</span>
                        <input type="date" value={filters.dateRange.updatedDate.start}
                          onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, updatedDate: { ...filters.dateRange.updatedDate, start: e.target.value } } })}
                          className="px-2 py-1 text-sm border border-gray-300 rounded" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">To:</span>
                        <input type="date" value={filters.dateRange.updatedDate.end}
                          onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, updatedDate: { ...filters.dateRange.updatedDate, end: e.target.value } } })}
                          className="px-2 py-1 text-sm border border-gray-300 rounded" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Consultant</label>
                  <select value={filters.consultant} onChange={(e) => setFilters({ ...filters, consultant: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded">
                    <option value="">All Consultants</option>
                    {consultants.map(cons => <option key={cons.id} value={cons.id}>{cons.name}</option>)}
                  </select>
                </div>

<div className="space-y-2">
  {([
    ['instructions', 'Instructions'],
    ['clientInfo', 'Client Info'],
    ['claims', 'Claims'],
    ['quotations', 'Quotations'],
    ['emails', 'Emails'],
    ['renewals', 'Renewals'],
    ['whatsapps', 'WhatsApps'],
    ['returnDebits', 'Return Debits'],
    ['products', 'Products'],
    ['policies', 'Policies']
  ] as const).map(([key, label]) => {
    const featureKey = REPORT_PERMISSION_MAP[key];
    if (!can(featureKey, 'read')) return null;
    return (
      <div key={key} className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={filters[key] as boolean}
          onChange={(e) => {
            // Radio-like behaviour: only one report type can be active.
            // Clear every other type when this one is checked.
            const next: ReportFilter = {
              ...filters,
              instructions: false,
              clientInfo: false,
              claims: false,
              quotations: false,
              emails: false,
              renewals: false,
              whatsapps: false,
              returnDebits: false,
              products: false,
              policies: false,
            };
            next[key] = e.target.checked;
            setFilters(next);
          }}
          className="w-4 h-4 text-blue-600 rounded border-gray-300"
        />
        <label className="text-sm font-medium text-gray-700">{label}</label>
      </div>
    );
  })}
</div>

              </div>

              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                  onClick={fetchReport}
                  disabled={loading || !(
                    filters.instructions || filters.clientInfo || filters.claims ||
                    filters.quotations || filters.emails || filters.renewals ||
                    filters.whatsapps || filters.returnDebits || filters.products || filters.policies
                  )}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>

                <button onClick={exportToExcel} disabled={reportData.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </button>
                <button onClick={exportToCSV} disabled={reportData.length === 0}
                  className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* RESULTS */}
            {reportData.length > 0 && (
              <div className="space-y-8">

                {/* INSTRUCTIONS */}
                {reportType === 'Instructions' && (() => {
                  const rows = reportData as InstructionReport[];
                  const open = rows.filter(r => r.status !== 'completed');
                  const completed = rows.filter(r => r.status === 'completed');
                  const renderTable = (list: InstructionReport[], title: string, headerClass: string) => (
                    <div key={title} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className={`px-6 py-4 ${headerClass} border-b border-gray-200`}>
                        <h3 className="text-lg font-semibold">{title} ({list.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Created Date','Instruction No','Client','Policy No','Type','Method','Description','Assigned To','Status','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {list.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.reference_number || `INS-${String(row.id).padStart(3, '0')}`}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.instruction_type || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.instruction_method || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{row.instruction || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.assigned_to_name || 'Unassigned'}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'instruction')}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleInstructionClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                  return (
                    <>
                      {open.length > 0 && renderTable(open, 'Open Instructions', 'bg-blue-50 text-blue-800')}
                      {completed.length > 0 && renderTable(completed, 'Completed Instructions', 'bg-green-50 text-green-800')}
                    </>
                  );
                })()}

                {/* CLAIMS */}
                {reportType === 'Claims' && (() => {
                  const rows = reportData as ClaimReport[];
                  const open = rows.filter(r => r.status?.toLowerCase() !== 'completed' && r.status?.toLowerCase() !== 'cancelled');
                  const completed = rows.filter(r => r.status?.toLowerCase() === 'completed');
                  const renderTable = (list: ClaimReport[], title: string, headerClass: string) => (
                    <div key={title} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className={`px-6 py-4 ${headerClass} border-b border-gray-200`}>
                        <h3 className="text-lg font-semibold">{title} ({list.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Created Date','Claim No','Client','Policy No','Type','Method','Description','Consultant','Status','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {list.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.reference_number || `CLM-${String(row.id).padStart(3, '0')}`}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.claim_type || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.claim_method || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.description}>{row.description || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'claim')}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleClaimClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                  return (
                    <>
                      {open.length > 0 && renderTable(open, 'Open Claims', 'bg-yellow-50 text-yellow-800')}
                      {completed.length > 0 && renderTable(completed, 'Completed Claims', 'bg-green-50 text-green-800')}
                    </>
                  );
                })()}

                {/* QUOTATIONS */}
                {reportType === 'Quotations' && (() => {
                  const rows = reportData as QuotationReport[];
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-blue-800">Quotations ({rows.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Created Date','Reference','Client','Consultant','Status','Premium','Broker Fee','Mgmt Fee','Inception','Updated','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.reference || `Q-${row.id}`}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'quote')}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{formatMoney(row.premium)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatMoney(row.broker_fee)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatMoney(row.management_fee)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.inception_date ? new Date(row.inception_date).toLocaleDateString() : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.updated_date ? format(new Date(row.updated_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleQuoteClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* RENEWALS */}
                {reportType === 'Renewals' && (() => {
                  const rows = reportData as RenewalReport[];
                  const open = rows.filter(r => r.status?.toLowerCase() !== 'completed');
                  const completed = rows.filter(r => r.status?.toLowerCase() === 'completed');
                  const renderTable = (list: RenewalReport[], title: string, headerClass: string) => (
                    <div key={title} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className={`px-6 py-4 ${headerClass} border-b border-gray-200`}>
                        <h3 className="text-lg font-semibold">{title} ({list.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Created Date','Renewal No','Client','Policy No','Method','Description','Consultant','Status','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {list.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.reference_number || `REN-${String(row.id).padStart(3, '0')}`}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.renewal_method || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.description}>{row.description || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'renewal')}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleRenewalClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                  return (
                    <>
                      {open.length > 0 && renderTable(open, 'Open Renewals', 'bg-yellow-50 text-yellow-800')}
                      {completed.length > 0 && renderTable(completed, 'Completed Renewals', 'bg-green-50 text-green-800')}
                    </>
                  );
                })()}

                {/* RETURN DEBITS */}
                {reportType === 'Return Debits' && (() => {
                  const rows = reportData as ReturnDebitReport[];
                  const open = rows.filter(r => r.status?.toLowerCase() !== 'completed');
                  const completed = rows.filter(r => r.status?.toLowerCase() === 'completed');
                  const renderTable = (list: ReturnDebitReport[], title: string, headerClass: string) => (
                    <div key={title} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className={`px-6 py-4 ${headerClass} border-b border-gray-200`}>
                        <h3 className="text-lg font-semibold">{title} ({list.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Created Date','Reference','Client','Policy No','Method','Description','Consultant','Resolution','Status','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {list.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.reference_number || `RD-${String(row.id).padStart(3, '0')}`}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.return_debit_method || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.description}>{row.description || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatResolution(row.resolution_method)}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'returnDebit')}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleReturnDebitClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Details">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                  return (
                    <>
                      {open.length > 0 && renderTable(open, 'Open Return Debits', 'bg-yellow-50 text-yellow-800')}
                      {completed.length > 0 && renderTable(completed, 'Completed Return Debits', 'bg-green-50 text-green-800')}
                    </>
                  );
                })()}

                                {/* POLICIES */}
                {reportType === 'Policies' && (() => {
                  const rows = reportData as PolicyReport[];
                  const active = rows.filter(r => r.status?.toLowerCase() === 'active');
                  const cancelled = rows.filter(r => r.status?.toLowerCase() === 'cancelled');
                  const other = rows.filter(r => r.status?.toLowerCase() !== 'active' && r.status?.toLowerCase() !== 'cancelled');

                  const renderTable = (list: PolicyReport[], title: string, headerClass: string) => (
                    <div key={title} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className={`px-6 py-4 ${headerClass} border-b border-gray-200`}>
                        <h3 className="text-lg font-semibold">{title} ({list.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Policy No','Client','Product','Insurer','Status','Start','End','Premium','Renewal','Consultant','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {list.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.product_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.insurer || '-'}</td>
                                <td className="px-6 py-4">{statusChip(row.status, 'claim')}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.start_date ? new Date(row.start_date).toLocaleDateString() : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.end_date ? new Date(row.end_date).toLocaleDateString() : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{formatMoney(row.premium_amount)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.renewal_date ? new Date(row.renewal_date).toLocaleDateString() : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handlePolicyClick(row.id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Policy">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      {active.length > 0 && renderTable(active, 'Active Policies', 'bg-green-50 text-green-800')}
                      {other.length > 0 && renderTable(other, 'Other Policies', 'bg-blue-50 text-blue-800')}
                      {cancelled.length > 0 && renderTable(cancelled, 'Cancelled Policies', 'bg-red-50 text-red-800')}
                    </>
                  );
                })()}

                {/* CLIENT INFO */}
                {reportType === 'Client Info' && (() => {
                  const rows = reportData as ClientInfoReport[];
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-blue-800">Clients ({rows.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Client No','Name','Email','Mobile','Type','Status','Consultant','Created','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.client_number || row.id}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{`${row.name || ''} ${row.surname || ''}`.trim() || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.email || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.mobile_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                  {row.personal ? 'Personal' : row.commercial ? 'Commercial' : row.medical ? 'Medical' : '-'}
                                </td>
                                <td className="px-6 py-4">{statusChip(row.status || '', 'claim')}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy') : '-'}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleClientClick(row.id)} className="text-blue-600 hover:text-blue-800" title="View Client">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                                {/* EMAILS */}
                {reportType === 'Emails' && (() => {
                  const rows = reportData as EmailReport[];
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-blue-800">Emails ({rows.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Date','Client','Sent To','Subject','Message','Employee','Status','Ref No','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy HH:mm') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.sent_to || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={row.subject || ''}>{row.subject || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.message || ''}>{row.message || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.employee || '-'}</td>
                                <td className="px-6 py-4">{statusChip(row.delivery_status || '', 'claim')}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.ref_no || '-'}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleEmailClick(row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Client Emails">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* WHATSAPPS */}
                {reportType === 'WhatsApps' && (() => {
                  const rows = reportData as WhatsAppReport[];
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 bg-green-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-green-800">WhatsApps ({rows.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Date','Client','Sent To','Message','Employee','Status','Ref No','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(row => (
                              <tr key={row.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-600">{row.created_date ? format(new Date(row.created_date), 'dd MMM yyyy HH:mm') : '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.sent_to || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.message || ''}>{row.message || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.employee || '-'}</td>
                                <td className="px-6 py-4">{statusChip(row.delivery_status || '', 'claim')}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.ref_no || '-'}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handleWhatsAppClick(row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Client WhatsApps">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* PRODUCTS */}
                {reportType === 'Products' && (() => {
                  const rows = reportData as ProductReport[];
                  return (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-6 py-4 bg-purple-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-purple-800">Products ({rows.length})</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{['Policy No','Client','Product Type','Description','Insured Value','Premium','Cover Type','Status','Consultant','Details'].map(th)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {rows.map(row => (
                              <tr key={`${row.product_type}-${row.product_id}`} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.policy_number || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{row.client_name || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.product_type || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.description || ''}>{row.description || '-'}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{formatMoney(row.insured_value)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{formatMoney(row.premium)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{row.cover_type || '-'}</td>
                                <td className="px-6 py-4">{statusChip(row.status || '', 'claim')}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{consultantDisplay(row.consultant_name, row.consultant_surname)}</td>
                                <td className="px-6 py-4">
                                  <button onClick={() => handlePolicyClick(row.policy_id, row.client_id)} className="text-blue-600 hover:text-blue-800" title="View Client Policies">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            {!loading && reportData.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-500">No data to display. Generate a report using the filters above.</p>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;